// Converts date choices into the complete periods a report can display.
// Calendar shortcuts and changes between Days/Weeks/Months supply date ranges.
// These helpers find which available report periods cover those dates and format
// their Start/End labels for the calendar and date-range summary.
import { DateTime } from 'luxon';
import { PeriodOption, Resolution } from '../report-settings/reportSettings.ts';
import { REPORT_TIME_ZONE } from './calendarModel.ts';

export type QuickRangeKey =
    | 'today'
    | 'yesterday'
    | 'this-week'
    | 'last-week'
    | 'last-7-days'
    | 'this-month'
    | 'last-month'
    | 'last-30-days'
    | 'this-year'
    | 'last-year'
    | 'last-365-days';

export interface PeriodIndexRange {
    firstIndex: number;
    lastIndex: number;
}

export function findPeriodForCalendarDate(periods: PeriodOption[], date: Date): PeriodOption | undefined {
    // The input's year/month/day fields are the date chosen in a calendar. Use those
    // fields as a Helsinki date; converting its timestamp could move it to another day.
    const selectedDate = DateTime.fromObject(
        {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate(),
        },
        { zone: REPORT_TIME_ZONE },
    );

    const matchingPeriod = periods.find((period) => {
        const start = DateTime.fromJSDate(period.start).setZone(REPORT_TIME_ZONE).startOf('day');
        const end = DateTime.fromJSDate(period.end).setZone(REPORT_TIME_ZONE).startOf('day');
        const containsDate = selectedDate >= start && selectedDate < end;
        return containsDate;
    });
    return matchingPeriod;
}

export function formatPeriodBoundary(
    period: PeriodOption,
    resolution: Resolution,
    boundary: 'start' | 'end',
): string {
    const start = DateTime.fromJSDate(period.start).setZone(REPORT_TIME_ZONE);
    const end = DateTime.fromJSDate(period.end).setZone(REPORT_TIME_ZONE);
    const boundaryDate = boundary === 'start' ? start : end;
    const direction = boundary === 'start' ? 'starting' : 'ending';
    const formattedDate = `${formatOrdinal(boundaryDate.day)} ${boundaryDate.toFormat('LLLL yyyy HH:mm')}`;

    if (resolution === 'Days') return formattedDate;
    if (resolution === 'Months') return `${start.toFormat('LLLL yyyy')}, ${direction} ${formattedDate}`;
    return `Week ${start.weekNumber}, ${direction} ${formattedDate}`;
}

export function moveNearestRangeBoundary(
    firstIndex: number,
    lastIndex: number,
    targetIndex: number,
): readonly [number, number] {
    // Clicking an existing endpoint selects just that period, rather than leaving the range intact.
    if (targetIndex === firstIndex || targetIndex === lastIndex) return [targetIndex, targetIndex];
    if (targetIndex < firstIndex) return [targetIndex, lastIndex];
    if (targetIndex > lastIndex) return [firstIndex, targetIndex];

    const distanceFromStart = targetIndex - firstIndex;
    const distanceFromEnd = lastIndex - targetIndex;
    const nextRange = distanceFromStart <= distanceFromEnd
        ? [targetIndex, lastIndex] as const
        : [firstIndex, targetIndex] as const;
    return nextRange;
}

export function findPeriodRangeForDates(
    periods: PeriodOption[],
    start: Date,
    end: Date,
): PeriodIndexRange | null {
    // Report periods and the requested date range both exclude their end timestamp.
    // A week ending when the requested range starts must therefore not be selected.
    // Select every overlapping period, including weeks/months only partly covered by the dates.
    const startTime = start.getTime();
    const endTime = end.getTime();
    const firstIndex = periods.findIndex((period) => period.end.getTime() > startTime);
    if (firstIndex === -1) return null;

    let lastIndex = firstIndex;
    while (lastIndex + 1 < periods.length && periods[lastIndex + 1].start.getTime() < endTime) {
        lastIndex += 1;
    }

    if (periods[firstIndex].start.getTime() >= endTime) return null;
    return { firstIndex, lastIndex };
}

export function getQuickRangeDates(key: QuickRangeKey, referenceDate: Date): { start: Date; end: Date } {
    // Calendar arithmetic in Helsinki preserves midnight boundaries across DST changes.
    const today = DateTime.fromJSDate(referenceDate).setZone(REPORT_TIME_ZONE).startOf('day');
    let start: DateTime;
    let end: DateTime;

    switch (key) {
        case 'today':
            start = today;
            end = today.plus({ days: 1 });
            break;
        case 'yesterday':
            start = today.minus({ days: 1 });
            end = today;
            break;
        case 'this-week':
            start = today.startOf('week');
            end = start.plus({ weeks: 1 });
            break;
        case 'last-week':
            end = today.startOf('week');
            start = end.minus({ weeks: 1 });
            break;
        case 'last-7-days':
            // Rolling ranges include today, so seven days means six preceding days plus today.
            start = today.minus({ days: 6 });
            end = today.plus({ days: 1 });
            break;
        case 'this-month':
            start = today.startOf('month');
            end = start.plus({ months: 1 });
            break;
        case 'last-month':
            end = today.startOf('month');
            start = end.minus({ months: 1 });
            break;
        case 'last-30-days':
            start = today.minus({ days: 29 });
            end = today.plus({ days: 1 });
            break;
        case 'this-year':
            start = today.startOf('year');
            end = start.plus({ years: 1 });
            break;
        case 'last-year':
            end = today.startOf('year');
            start = end.minus({ years: 1 });
            break;
        case 'last-365-days':
            start = today.minus({ days: 364 });
            end = today.plus({ days: 1 });
            break;
    }

    return {
        start: start.set({ hour: 6 }).toJSDate(),
        end: end.set({ hour: 6 }).toJSDate(),
    };
}

function formatOrdinal(day: number): string {
    const remainder = day % 100;
    if (remainder >= 11 && remainder <= 13) return `${day}th`;
    if (day % 10 === 1) return `${day}st`;
    if (day % 10 === 2) return `${day}nd`;
    if (day % 10 === 3) return `${day}rd`;
    return `${day}th`;
}
